import type { Finding } from '@/portalApi'
import type { Severity } from '@/types/constants/severities'
import { portalApi } from '@/util/apiClient'
import { queryClient } from '@/util/queryClient'
import { useQuery } from '@tanstack/vue-query'
import { computed, type ComputedRef, type Ref } from 'vue'

const pollingMS = 30000
const limit = 10

export type FindingsBadge = { label: string; severity: Severity }

type HighSeverityFindings = {
  /** The most recent open findings, critical before high, at most `limit`. */
  findings: ComputedRef<Finding[] | undefined>
  /** Counts what `findings` shows, as "10+" past `limit`. */
  badge: ComputedRef<FindingsBadge | undefined>
  error: Ref<Error | null>
}

const getMostRecent = (severity: Severity) =>
  portalApi.findingsGet({
    query: {
      limit,
      order_by: 'first_event_timestamp',
      order_is_desc: true,
      queryables: { severity: [severity], status__ne: 'CLOSED' },
    },
  })

/**
 * Open critical/high findings, as the dashboard's "Recent critical/high
 * severity findings" table and the sidebar's Findings badge show them.
 */
const useHighSeverityFindings = (): HighSeverityFindings => {
  const { data, error } = useQuery(
    {
      queryKey: ['findings', 'recentHighSeverity'],
      queryFn: () =>
        Promise.all([getMostRecent('CRITICAL'), getMostRecent('HIGH')]),
      refetchInterval: pollingMS,
    },
    queryClient,
  )

  const findings = computed(() =>
    data.value?.flatMap(({ results }) => results || []).slice(0, limit),
  )

  const badge = computed(() => {
    if (!data.value) return undefined
    const [critical, high] = data.value.map(({ count }) => count || 0)
    const total = critical + high
    if (!total) return undefined
    return {
      label: total > limit ? `${limit}+` : `${total}`,
      severity: critical ? 'CRITICAL' : 'HIGH',
    } satisfies FindingsBadge
  })

  return { findings, badge, error }
}

export default useHighSeverityFindings
